import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SelectChangeEvent } from '@mui/material/Select';

import { headerActions } from 'app/pages/Header/slice';
import { selectSelectedAgentType } from 'app/pages/Header/selectors';
import {
  AGENT_TYPE_CHANGE_EVENT,
  AgentTypeValue,
  getAgentType,
  setAgentType,
} from 'app/utils/agentType';
import queryClient from 'app/apiService/queryClient';
import { AGENT_TYPE_NONE_VALUE } from './constants';

/**
 * Owns all logic/state for the AgentTypeDropdown.
 *
 * Responsibilities:
 *   - Read the current selection (Redux is source of truth for the UI).
 *   - Persist user changes to localStorage (so the HTTP layer can read it).
 *   - Invalidate the React Query cache on change so every page refetches
 *     with the new `agent_type` query param applied.
 *   - Stay in sync with cross-tab changes via `storage` event.
 *   - On selection change, do a full page reload so every saga / hook /
 *     useEffect across the app re-runs with the new filter, mirroring the
 *     "everything refreshes" UX of the dealer dropdown.
 */
export const useAgentTypeDropdown = () => {
  const dispatch = useDispatch();
  const selectedAgentType = useSelector(selectSelectedAgentType);

  // Keep Redux in sync if localStorage was already populated before
  // the slice initialised (e.g. dropdown mounted from a different layout).
  React.useEffect(() => {
    const storedValue = getAgentType();
    if ((storedValue || null) !== (selectedAgentType || null)) {
      dispatch(headerActions.setSelectedAgentType(storedValue));
    }
    // Intentionally run once on mount.
  }, []);

  // Cross-tab + intra-app sync: react to setAgentType() from any context.
  React.useEffect(() => {
    const handleChange = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const nextValue: string | null = detail?.value ?? null;
      dispatch(headerActions.setSelectedAgentType(nextValue));
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key && event.key !== 'selected_agent_type') return;
      dispatch(headerActions.setSelectedAgentType(getAgentType()));
    };

    window.addEventListener(AGENT_TYPE_CHANGE_EVENT, handleChange);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(AGENT_TYPE_CHANGE_EVENT, handleChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, [dispatch]);

  const selectAgentType = React.useCallback(
    (rawValue: string) => {
      const nextValue: AgentTypeValue | null =
        rawValue && rawValue !== AGENT_TYPE_NONE_VALUE
          ? (rawValue as AgentTypeValue)
          : null;

      const currentValue = getAgentType();
      if ((currentValue || null) === (nextValue || null)) {
        return;
      }

      setAgentType(nextValue);
      dispatch(headerActions.setSelectedAgentType(nextValue));

      try {
        queryClient.removeQueries();
      } catch (error) {
        console.error('AgentTypeDropdown cache invalidation error:', error);
      }

      try {
        window.location.reload();
      } catch (error) {
        console.error('AgentTypeDropdown reload error:', error);
      }
    },
    [dispatch],
  );

  const handleChange = React.useCallback(
    (event: SelectChangeEvent<string>) => {
      selectAgentType(event.target.value);
    },
    [selectAgentType],
  );

  const value: string = selectedAgentType ?? AGENT_TYPE_NONE_VALUE;

  return {
    value,
    handleChange,
    selectAgentType,
  };
};
